import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Leaf, Truck, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";

const features = [
  { icon: Leaf, title: "6 enzime naturale", text: "Formulă concentrată care curăță în profunzime și protejează fibrele." },
  { icon: Sparkles, title: "Până la 133 spălări", text: "Un singur bidon de 4L îți ajunge pentru zeci de spălări." },
  { icon: Truck, title: "Livrare în toată țara", text: "Comandă rapid, livrăm oriunde prin curier cu plata la livrare." },
  { icon: ShieldCheck, title: "Parfum de durată", text: "Rufe proaspete și catifelate, cu un parfum care persistă." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [heroImage, setHeroImage] = useState("/products/hero-default.png");
  const [offerBanner, setOfferBanner] = useState("https://customer-assets.emergentagent.com/job_mimaso-store/artifacts/cgovscdx_C15C0DE9-21FD-4F3B-B79D-B79F0862043E.png");
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.get("/products").then((res) => {
      const all = res.data || [];
      const feat = all.filter((p) => p.featured);
      const rest = all.filter((p) => !p.featured);
      setFeatured([...feat, ...rest].slice(0, 4));
    });
    api.get("/settings").then((res) => {
      if (res.data.hero_image) setHeroImage(res.data.hero_image);
      if (res.data.offer_banner_image) setOfferBanner(res.data.offer_banner_image);
      setGallery(res.data.gallery || []);
    });
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bubble-bg relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 lg:grid-cols-2 lg:px-12 lg:py-24">
          <div className="animate-fade-up">
            <span className="label-caps inline-flex items-center gap-2 rounded-full bg-sky-100 px-4 py-1.5 text-sky-700">
              <Sparkles className="h-3.5 w-3.5" /> Detergenți premium concentrați
            </span>
            <h1 className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Curățenie profesională,<br />
              <span className="text-sky-600">direct la tine acasă</span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
              Detergenți și balsamuri concentrate, cu formule cu enzime naturale.
              Curățenie profesională, direct de la producător la tine acasă — calitate premium,
              prețuri corecte și livrare rapidă în toată țara.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-sky-600 px-7 text-base hover:bg-sky-500" data-testid="hero-cta-products">
                <Link to="/produse">Vezi produsele <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="animate-pop rounded-full bg-red-600 px-7 text-base text-white shadow-lg shadow-red-600/30 hover:bg-red-700" data-testid="hero-cta-offers">
                <Link to="/oferte">Ofertele noastre</Link>
              </Button>
            </div>
          </div>
          <div className="relative animate-fade-up">
            <div className="absolute inset-0 -z-10 rounded-[2.5rem] bg-gradient-to-br from-sky-100 to-emerald-50" />
            <img
              src={heroImage}
              alt="Detergent Mimaso"
              className="mx-auto h-[420px] w-auto object-contain drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* OFFER BANNER */}
      <section className="mx-auto max-w-7xl px-6 pb-4 pt-10 lg:px-12">
        <Link
          to="/oferte"
          className="group relative block overflow-hidden rounded-3xl shadow-[0_8px_32px_rgba(2,132,199,0.12)]"
          data-testid="offer-banner-cta"
        >
          <img
            src={offerBanner}
            alt="Promoție 2+1 GRATIS - 2 detergenți gel + 1 balsam de rufe"
            className="w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
          />
          <span className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-emerald-700 shadow-lg transition-colors group-hover:bg-emerald-50 sm:bottom-8 sm:right-8 sm:text-base">
            Vezi pachetele <ArrowRight className="h-4 w-4" />
          </span>
        </Link>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-12">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <span className="label-caps text-sky-600">Cele mai cerute</span>
            <h2 className="mt-2 font-display text-2xl font-black text-slate-900 sm:text-3xl lg:text-4xl">Produse recomandate</h2>
          </div>
          <Button asChild variant="ghost" className="hidden text-sky-600 hover:text-sky-700 sm:inline-flex" data-testid="featured-see-all">
            <Link to="/produse">Vezi toate <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* GALERIE */}
      {gallery.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-12 lg:px-12" data-testid="home-gallery">
          <div className="mb-8">
            <span className="label-caps text-sky-600">Galerie</span>
            <h2 className="mt-2 font-display text-2xl font-black text-slate-900 sm:text-3xl lg:text-4xl">Produsele noastre în imagini</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {gallery.map((g) => (
              <div key={g.id} className="group aspect-square overflow-hidden rounded-2xl border border-slate-100 bg-sky-50/40">
                <img src={g.url} alt="Galerie Mimaso" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FEATURES (jos de tot) */}
      <section className="border-t border-slate-100 bg-slate-50/50">
        <div className="mx-auto grid max-w-7xl gap-6 px-6 py-14 sm:grid-cols-2 lg:grid-cols-4 lg:px-12">
          {features.map((f) => (
            <div key={f.title} className="rounded-2xl bg-white p-6 shadow-[0_8px_32px_rgba(2,132,199,0.05)]">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-sky-100 text-sky-600">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{f.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{f.text}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
